import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { LegalPreview } from "@/components/legal/legal-preview";
import { useAuth } from "@/hooks/use-auth";

interface LegalConfig {
  readonly businessName: string;
  readonly registrationNumber: string;
  readonly address: string;
  readonly phone: string;
  readonly email: string;
  readonly additionalClauses?: string;
}

interface DeletionRequestRow {
  readonly id: string;
  readonly phone: string;
  readonly email: string | null;
  readonly reason: string | null;
  readonly status: string;
  readonly requested_at: string;
  readonly must_complete_by: string;
  readonly completed_at: string | null;
}

const EMPTY_CONFIG: LegalConfig = {
  businessName: "",
  registrationNumber: "",
  address: "",
  phone: "",
  email: "",
};

export function LegalSettingsPage(): ReactNode {
  const { accessToken } = useAuth();
  const [config, setConfig] = useState<LegalConfig>(EMPTY_CONFIG);
  const [deletionRequests, setDeletionRequests] = useState<
    ReadonlyArray<DeletionRequestRow>
  >([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const headers = {
    "Content-Type": "application/json",
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };

  // Load config and deletion requests
  useEffect(() => {
    async function load(): Promise<void> {
      try {
        const [configRes, deletionRes] = await Promise.all([
          fetch("/api/legal/config", { headers }),
          fetch("/api/legal/deletion-requests", { headers }),
        ]);

        if (configRes.ok) {
          const configJson = (await configRes.json()) as {
            data: LegalConfig | null;
          };
          if (configJson.data) {
            setConfig(configJson.data);
          }
        }

        if (deletionRes.ok) {
          const deletionJson = (await deletionRes.json()) as {
            data: ReadonlyArray<DeletionRequestRow>;
          };
          setDeletionRequests(deletionJson.data);
        }
      } catch {
        // Network error — leave defaults
      }
    }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch("/api/legal/config", {
        method: "PUT",
        headers,
        body: JSON.stringify(config),
      });
      if (res.ok) {
        setMessage("ההגדרות נשמרו בהצלחה");
      } else {
        const err = (await res.json()) as { error?: string };
        setMessage(err.error ?? "שגיאה בשמירה");
      }
    } catch {
      setMessage("שגיאת רשת");
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, accessToken]);

  const handleCompleteDeletion = useCallback(
    async (id: string) => {
      try {
        const res = await fetch(`/api/legal/deletion-requests/${id}/complete`, {
          method: "PUT",
          headers,
        });
        if (res.ok) {
          setDeletionRequests((prev) =>
            prev.map((r) =>
              r.id === id ? { ...r, status: "completed" } : r,
            ),
          );
        }
      } catch {
        // Network error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken],
  );

  const updateField = useCallback(
    (field: keyof LegalConfig) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfig((prev) => ({ ...prev, [field]: e.target.value }));
    },
    [],
  );

  const pendingRequests = deletionRequests.filter(
    (r) => r.status === "pending",
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <h1 className="text-2xl font-bold">{"משפטי ונגישות"}</h1>

      {/* Business details form */}
      <Card>
        <CardHeader>
          <CardTitle>{"פרטי העסק לעמודים משפטיים"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">{"שם העסק"}</label>
              <Input
                value={config.businessName}
                onChange={updateField("businessName")}
                placeholder="שם העסק המלא"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {"מספר רישום (ח.פ./ע.מ.)"}
              </label>
              <Input
                value={config.registrationNumber}
                onChange={updateField("registrationNumber")}
                placeholder="מספר רישום"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{"כתובת"}</label>
              <Input
                value={config.address}
                onChange={updateField("address")}
                placeholder="כתובת העסק"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{"טלפון"}</label>
              <Input
                value={config.phone}
                onChange={updateField("phone")}
                placeholder="05XXXXXXXX"
                dir="ltr"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-sm font-medium">{"דוא״ל"}</label>
              <Input
                value={config.email}
                onChange={updateField("email")}
                placeholder="email@example.com"
                dir="ltr"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="min-h-[44px]"
            >
              {saving ? "שומר..." : "שמירה"}
            </Button>
            {message !== null && (
              <span className="text-sm text-muted-foreground">{message}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Legal page previews */}
      <Card>
        <CardHeader>
          <CardTitle>{"תצוגה מקדימה של עמודים משפטיים"}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="privacy" dir="rtl">
            <TabsList className="w-full md:w-auto">
              <TabsTrigger value="privacy">{"פרטיות"}</TabsTrigger>
              <TabsTrigger value="terms">{"תנאי שימוש"}</TabsTrigger>
              <TabsTrigger value="accessibility">{"נגישות"}</TabsTrigger>
            </TabsList>

            <TabsContent value="privacy">
              <LegalPreview pageType="privacy" tenantSlug="" />
            </TabsContent>
            <TabsContent value="terms">
              <LegalPreview pageType="terms" tenantSlug="" />
            </TabsContent>
            <TabsContent value="accessibility">
              <LegalPreview pageType="accessibility" tenantSlug="" />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Deletion requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {"בקשות מחיקת מידע"}
            {pendingRequests.length > 0 && (
              <Badge variant="destructive">{pendingRequests.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {deletionRequests.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {"אין בקשות מחיקה"}
            </p>
          ) : (
            <div className="space-y-3">
              {deletionRequests.map((req) => (
                <div
                  key={req.id}
                  className="flex flex-col gap-2 rounded-lg border p-3 md:flex-row md:items-center md:justify-between"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium" dir="ltr">
                        {req.phone}
                      </span>
                      <Badge
                        variant={
                          req.status === "pending" ? "destructive" : "secondary"
                        }
                      >
                        {req.status === "pending" ? "ממתין" : "בוצע"}
                      </Badge>
                    </div>
                    {req.reason && (
                      <p className="text-sm text-muted-foreground">
                        {req.reason}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {"יש לבצע עד: "}
                      {new Date(req.must_complete_by).toLocaleDateString(
                        "he-IL",
                      )}
                    </p>
                  </div>
                  {req.status === "pending" && (
                    <Button
                      size="sm"
                      onClick={() => void handleCompleteDeletion(req.id)}
                      className="min-h-[44px]"
                    >
                      {"בוצע"}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
